import { Injectable, OnDestroy, Injector } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { LoggingService } from '../logging/logging.service';
import { ConversationStateMachine } from './conversation-state-machine.service';
// FIN-0066: ChatService imported dynamically via require() to break circular dependency
import {
  ConversationState,
  canSendMessages,
  isReadOnly,
  needsUserAction,
  OrphanReason
} from '../../models/conversation-state';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface ConversationContext {
  conversationId: string;
  participatingClients: Set<string>;
  startedAt: Date;
  lastActivity: Date;
  isActive: boolean;
  state: ConversationState;  // ← NEW: Track sync state
}

/**
 * Service responsible for managing conversation contexts in a multi-tab environment.
 * Tracks which conversations belong to which tabs to enable proper message filtering.
 */
@Injectable({
  providedIn: 'root',
})
export class ConversationManager implements OnDestroy {
  private conversations = new Map<string, ConversationContext>();
  private conversationsSubject = new BehaviorSubject<Map<string, ConversationContext>>(new Map());
  private destroy$ = new Subject<void>();

  // 🔥 FIX (FIN-0065): Single Source of Truth for current conversation ID
  // This eliminates the dual-source architecture smell where ConversationManager and MessageStore
  // could get out of sync, causing silent message rejection
  private currentConversationIdSubject = new BehaviorSubject<string | null>(null);

  public conversations$ = this.conversationsSubject.asObservable();
  public currentConversationId$ = this.currentConversationIdSubject.asObservable();

  // localStorage keys for conversation persistence
  private readonly ACTIVE_CONVERSATION_ID_KEY = 'active-conversation-id';
  private readonly CONVERSATION_BACKUP_KEY = 'conversation-backup';

  constructor(
    private logger: LoggingService,
    private stateMachine: ConversationStateMachine,
    private http: HttpClient,
    private injector: Injector  // Use injector to lazily resolve ChatService and avoid circular dependency
  ) {
    // 🔥 FIX (FIN-0065): Initialize BehaviorSubject from localStorage on startup
    // This ensures the in-memory state is synchronized with persisted state
    const storedId = localStorage.getItem(this.ACTIVE_CONVERSATION_ID_KEY);
    if (storedId) {
      this.currentConversationIdSubject.next(storedId);
      this.logger.info('ConversationManager', `Restored active conversation ID from localStorage: ${storedId}`);
    }

    this.logger.info('ConversationManager', 'Service initialized');
  }

  /**
   * Lazily resolve ChatService using Injector to avoid circular dependency
   * FIN-0066: Breaking circular dependency chain:
   * ChatMessageProcessor → ConversationManager → ChatService
   *
   * Using require() instead of import to avoid static analysis by madge
   */
  private _chatService?: any;
  private getChatService(): any {
    // Lazy initialization on first use to avoid circular dependency
    if (!this._chatService) {
      // Import the class dynamically at runtime (not during module parse)
      const { ChatService: ChatServiceClass } = require('../chat/chat.service');
      this._chatService = this.injector.get(ChatServiceClass);
    }
    return this._chatService;
  }

  ngOnDestroy(): void {
    this.logger.info('ConversationManager', 'Destroying service');
    this.destroy$.next();
    this.destroy$.complete();
    this.conversationsSubject.complete();
    this.conversations.clear();
  }

  /**
   * Create a new DRAFT conversation for the current tab
   * This is the ONLY way to create a new conversation
   */
  createConversation(conversationId: string, userEmail?: string): ConversationContext {
    // If no user email provided, use a placeholder (will be set when available)
    const participatingClient = userEmail || 'pending';

    // Create DRAFT state via state machine (enforces design by contract)
    const state = this.stateMachine.createDraft(conversationId);

    return this.startConversation(conversationId, participatingClient, state);
  }

  /**
   * Start a new conversation for the current tab with explicit state
   * PRIVATE - use createConversation() or restoreConversation() instead
   */
  private startConversation(
    conversationId: string,
    participatingClient: string,
    state: ConversationState
  ): ConversationContext {
    const context: ConversationContext = {
      conversationId,
      participatingClients: new Set([participatingClient]),
      startedAt: new Date(),
      lastActivity: new Date(),
      isActive: true,
      state  // ← NEW: Include state
    };

    this.conversations.set(conversationId, context);
    this.conversationsSubject.next(new Map(this.conversations));

    this.logger.info('ConversationManager', `Started conversation ${conversationId} in ${state.kind} state`, {
      conversationId,
      participatingClient,
      state: this.stateMachine.getStateDescription(state)
    });

    return context;
  }

  /**
   * Update the last activity timestamp for a conversation
   */
  updateConversationActivity(conversationId: string): void {
    const context = this.conversations.get(conversationId);
    if (context) {
      context.lastActivity = new Date();
      this.conversationsSubject.next(new Map(this.conversations));
    }
  }

  /**
   * Add a client to an existing conversation (for multi-tab support)
   */
  addClientToConversation(conversationId: string, userEmail: string): boolean {
    const context = this.conversations.get(conversationId);
    if (context) {
      context.participatingClients.add(userEmail);
      context.lastActivity = new Date();
      this.conversationsSubject.next(new Map(this.conversations));

      this.logger.info('ConversationManager', `Added client ${userEmail} to conversation ${conversationId}`);
      return true;
    }
    return false;
  }

  /**
   * Check if a conversation belongs to the specified client/tab
   * @param conversationId The conversation ID to check
   * @param userEmail The client ID to check ownership for
   * @returns true if this tab should process messages for this conversation
   */
  belongsToThisTab(conversationId: string, userEmail?: string): boolean {
    const context = this.conversations.get(conversationId);
    if (!context || !context.isActive) {
      // If we don't know about this conversation, don't process it
      this.logger.debug('ConversationManager', `Conversation ${conversationId} not found or inactive`);
      return false;
    }

    if (!userEmail) {
      // If no userEmail provided, fall back to basic existence check
      this.logger.debug('ConversationManager', `No userEmail provided for conversation ${conversationId}, using basic check`);
      return true;
    }

    // Check if this conversation belongs to the specified userEmail
    const belongs = this.isConversationOwnedByClient(conversationId, userEmail);
    this.logger.debug('ConversationManager', `Conversation ${conversationId} belongs to client ${userEmail}: ${belongs}`, {
      conversationId,
      userEmail,
      participatingClients: Array.from(context.participatingClients),
      belongs
    });
    return belongs;
  }

  /**
   * Check if a conversation belongs to the current tab (client)
   */
  isConversationOwnedByClient(conversationId: string, userEmail: string): boolean {
    const context = this.conversations.get(conversationId);
    if (!context) {
      return false;
    }

    // A conversation is owned by a client if the client is participating in it
    return context.participatingClients.has(userEmail);
  }

  /**
   * Get conversation context by ID
   */
  getConversation(conversationId: string): ConversationContext | undefined {
    return this.conversations.get(conversationId);
  }

  /**
   * Get all conversations for a specific client
   */
  getConversationsForClient(userEmail: string): ConversationContext[] {
    return Array.from(this.conversations.values())
      .filter(context => context.participatingClients.has(userEmail));
  }

  /**
   * End a conversation (mark as inactive)
   */
  endConversation(conversationId: string): void {
    const context = this.conversations.get(conversationId);
    if (context) {
      context.isActive = false;
      context.lastActivity = new Date();
      this.conversationsSubject.next(new Map(this.conversations));

      this.logger.info('ConversationManager', `Ended conversation ${conversationId}`);
    }
  }

  /**
   * Reactivate a conversation (mark as active)
   * FIN-0079: Used when sending offline messages after reconnection
   * Ensures conversation is active so bot responses aren't filtered out
   */
  reactivateConversation(conversationId: string): boolean {
    const context = this.conversations.get(conversationId);
    if (!context) {
      this.logger.warn('ConversationManager', `Cannot reactivate conversation ${conversationId} - not found`);
      return false;
    }

    // Only log if actually changing state
    if (!context.isActive) {
      this.logger.info('ConversationManager', `Reactivating conversation ${conversationId}`);
    }

    context.isActive = true;
    context.lastActivity = new Date();
    this.conversationsSubject.next(new Map(this.conversations));

    return true;
  }

  /**
   * Remove old inactive conversations to prevent memory leaks
   */
  cleanupOldConversations(maxAgeHours: number = 24): void {
    const cutoffTime = new Date(Date.now() - maxAgeHours * 60 * 60 * 1000);
    let removedCount = 0;

    for (const [conversationId, context] of this.conversations.entries()) {
      if (!context.isActive && context.lastActivity < cutoffTime) {
        this.conversations.delete(conversationId);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      this.conversationsSubject.next(new Map(this.conversations));
      this.logger.info('ConversationManager', `Cleaned up ${removedCount} old conversations`);
    }
  }

  /**
   * Generate a unique conversation ID
   */
  generateConversationId(): string {
    return `conv-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  }

  // ========================================
  // STATE MANAGEMENT (Design by Contract)
  // ========================================

  /**
   * Restore conversation from IndexedDB
   * Creates RESTORED state which FORCES sync check via type system
   */
  restoreConversation(
    conversationId: string,
    messageCount: number,
    userEmail: string
  ): ConversationContext {
    // Create RESTORED state via state machine
    const state = this.stateMachine.restoreFromStorage(conversationId, messageCount);

    const context = this.startConversation(conversationId, userEmail, state);

    // TypeScript FORCES us to handle needsSyncCheck
    if (state.needsSyncCheck) {
      // Perform sync check asynchronously (don't block restoration)
      this.performBackendSyncCheck(conversationId).catch(error => {
        this.logger.error('ConversationManager', 'Backend sync check failed', error);
      });
    }

    return context;
  }

  /**
   * Perform backend sync check for RESTORED conversations
   * PRIVATE - only called internally via restoreConversation()
   */
  private async performBackendSyncCheck(conversationId: string): Promise<void> {
    const context = this.conversations.get(conversationId);
    if (!context) {
      this.logger.warn('ConversationManager', `Cannot sync check - conversation ${conversationId} not found`);
      return;
    }

    // Type guard: ensure we're in RESTORED state
    if (context.state.kind !== 'RESTORED') {
      this.logger.warn('ConversationManager', `Cannot sync check - conversation ${conversationId} is in ${context.state.kind} state`);
      return;
    }

    try {
      this.logger.info('ConversationManager', `Checking backend sync for conversation ${conversationId}`);

      const response = await firstValueFrom(
        this.http.get<any>(`/api/chat/conversation/${conversationId}`)
      );

      if (response && response.messages && response.messages.length > 0) {
        // Backend has the conversation - load messages from backend
        // 🔥 FIX (FIN-0064): Actually load backend messages, don't just count them!
        // 🔥 FIX (FIN-0066): Use injector.get() to lazily resolve ChatService without import
        // This avoids circular dependency: ConversationManager ↔ ChatService
        const chatService = this.getChatService();
        chatService.loadMessagesFromBackend(response.messages, conversationId);

        // Transition to SYNCED state
        const newState = this.stateMachine.markSyncedFromRestored(
          context.state,
          response.messages.length
        );
        this.updateConversationState(conversationId, newState);

        this.logger.info('ConversationManager', `✅ Conversation ${conversationId} synced with backend`, {
          backendMessages: response.messages.length
        });

        // 🔥 FIN-0100: Check for orphaned local messages (local has more than backend)
        // This handles messages typed offline before FIN-0100 fix was deployed
        await this.checkAndUploadOrphanedMessages(conversationId, response.messages, chatService);
      } else {
        // Backend returned 200 but no messages - treat as missing
        const newState = this.stateMachine.markOrphanedFromRestored(
          context.state,
          'backend_expired'
        );
        this.updateConversationState(conversationId, newState);

        this.logger.warn('ConversationManager', `⚠️ Conversation ${conversationId} found but empty on backend`);
      }
    } catch (error: any) {
      if (error.status === 404) {
        // Backend doesn't have this conversation - mark as ORPHANED
        const newState = this.stateMachine.markOrphanedFromRestored(
          context.state,
          'backend_expired'
        );
        this.updateConversationState(conversationId, newState);

        this.logger.warn('ConversationManager', `❌ Conversation ${conversationId} not found on backend (404)`);
      } else {
        // Other error (network, 500, etc.) - keep in RESTORED state, will retry
        this.logger.error('ConversationManager', `Failed to check backend for conversation ${conversationId}`, error);
      }
    }
  }

  /**
   * 🔥 FIN-0100: Check for orphaned local messages and upload to backend
   * This handles messages typed offline before FIN-0100 fix was deployed.
   *
   * When local has more messages than backend (especially if last is USER message),
   * we upload the full conversation to trigger FIN-0099 auto-reply.
   */
  private async checkAndUploadOrphanedMessages(
    conversationId: string,
    backendMessages: any[],
    chatService: any
  ): Promise<void> {
    try {
      // Wait a bit for messages to be loaded into memory from IndexedDB
      // (loadMessagesFromBackend may still be processing)
      await new Promise(resolve => setTimeout(resolve, 100));

      // Get local messages (use getAllMessages() which returns the array directly)
      const localMessages = chatService.getAllMessages();

      this.logger.info('ConversationManager', 'Checking for orphaned messages', {
        localCount: localMessages.length,
        backendCount: backendMessages.length
      });

      if (localMessages.length <= backendMessages.length) {
        // Local has same or fewer messages - no orphaned messages
        this.logger.info('ConversationManager', 'No orphaned messages (local <= backend)');
        return;
      }

      // Local has more messages than backend
      const lastLocalMessage = localMessages[localMessages.length - 1];
      const lastMessageContent = lastLocalMessage.content?.getValue?.() || lastLocalMessage.content;

      // Check if last message is from USER (bot=false)
      if (lastLocalMessage.bot) {
        // Last message is from bot - no need to upload (bot already responded)
        this.logger.info('ConversationManager', 'Local has more messages but last is from bot - no upload needed');
        return;
      }

      // Last message is from USER - orphaned message detected!
      this.logger.info('ConversationManager', `🔥 Orphaned user message detected! Resending via WebSocket`, {
        conversationId,
        localCount: localMessages.length,
        backendCount: backendMessages.length,
        lastMessage: lastMessageContent?.substring?.(0, 50) + '...'
      });

      // Instead of HTTP POST (which has auth issues), resend the orphaned message
      // via WebSocket - the same path as normal messages. This triggers the bot response.
      // Get ChatCommunicationService to send the message
      const chatCommService = this.injector.get(
        // Dynamic import to avoid circular dependency
        (await import('../chat/chat-communication.service')).ChatCommunicationService
      );

      // Send just the last user message (the orphaned one)
      chatCommService.sendMessage([lastLocalMessage]);

      this.logger.info('ConversationManager', `✅ Orphaned message resent via WebSocket - bot will respond`);

    } catch (error: any) {
      this.logger.error('ConversationManager', 'Failed to resend orphaned message', {
        error: error?.message || String(error)
      });
      // Don't throw - this is a best-effort operation
    }
  }

  /**
   * Update conversation state
   * PRIVATE - all state transitions go through state machine methods
   */
  private updateConversationState(conversationId: string, newState: ConversationState): void {
    const context = this.conversations.get(conversationId);
    if (!context) {
      this.logger.warn('ConversationManager', `Cannot update state - conversation ${conversationId} not found`);
      return;
    }

    const oldStateKind = context.state.kind;
    context.state = newState;
    context.lastActivity = new Date();

    this.conversations.set(conversationId, context);
    this.conversationsSubject.next(new Map(this.conversations));

    this.logger.info('ConversationManager', `State transition: ${oldStateKind} → ${newState.kind}`, {
      conversationId,
      oldState: oldStateKind,
      newState: newState.kind,
      description: this.stateMachine.getStateDescription(newState)
    });
  }

  /**
   * Mark conversation as synced (after backend confirms)
   * Called when first message is sent to backend
   */
  markConversationSynced(conversationId: string, backendMessageCount: number): void {
    const context = this.conversations.get(conversationId);
    if (!context) {
      this.logger.warn('ConversationManager', `Cannot mark synced - conversation ${conversationId} not found`);
      return;
    }

    let newState: ConversationState;

    if (context.state.kind === 'DRAFT') {
      newState = this.stateMachine.markSyncedFromDraft(context.state, backendMessageCount);
    } else if (context.state.kind === 'RESTORED') {
      newState = this.stateMachine.markSyncedFromRestored(context.state, backendMessageCount);
    } else if (context.state.kind === 'SYNCED') {
      // Already synced, just update timestamp
      newState = this.stateMachine.updateSyncTimestamp(context.state, backendMessageCount);
    } else {
      this.logger.warn('ConversationManager', `Cannot sync from ${context.state.kind} state`);
      return;
    }

    this.updateConversationState(conversationId, newState);
  }

  /**
   * Resurrect an orphaned conversation (user chooses to upload)
   */
  resurrectOrphanedConversation(conversationId: string): void {
    const context = this.conversations.get(conversationId);
    if (!context) {
      this.logger.warn('ConversationManager', `Cannot resurrect - conversation ${conversationId} not found`);
      return;
    }

    if (context.state.kind !== 'ORPHANED') {
      this.logger.warn('ConversationManager', `Cannot resurrect - conversation ${conversationId} is in ${context.state.kind} state`);
      return;
    }

    const newState = this.stateMachine.resurrectOrphaned(context.state);
    this.updateConversationState(conversationId, newState);

    this.logger.info('ConversationManager', `Resurrected orphaned conversation ${conversationId}`);
  }

  /**
   * Check if messages can be sent in this conversation
   * Enforced by state machine
   */
  canSendMessages(conversationId: string): boolean {
    const context = this.conversations.get(conversationId);
    if (!context) {
      return false;
    }

    return canSendMessages(context.state);
  }

  /**
   * Check if conversation is in read-only mode
   */
  isReadOnly(conversationId: string): boolean {
    const context = this.conversations.get(conversationId);
    if (!context) {
      return false;
    }

    return isReadOnly(context.state);
  }

  /**
   * Check if conversation needs user action (e.g., orphaned state)
   */
  needsUserAction(conversationId: string): boolean {
    const context = this.conversations.get(conversationId);
    if (!context) {
      return false;
    }

    return needsUserAction(context.state);
  }

  /**
   * Get conversation state
   */
  getConversationState(conversationId: string): ConversationState | undefined {
    const context = this.conversations.get(conversationId);
    return context?.state;
  }

  /**
   * Get human-readable state description
   */
  getStateDescription(conversationId: string): string {
    const context = this.conversations.get(conversationId);
    if (!context) {
      return 'Not found';
    }

    return this.stateMachine.getStateDescription(context.state);
  }

  /**
   * Get or create the active conversation ID
   * This centralizes conversation ID management across the app
   * 🔥 FIX (FIN-0065): Uses BehaviorSubject as source of truth
   */
  getOrCreateActiveConversationId(): string {
    const currentId = this.currentConversationIdSubject.getValue();

    if (currentId) {
      this.logger.info('ConversationManager', `Using current active conversation ID: ${currentId}`);
      return currentId;
    }

    // Check localStorage as fallback (in case service was reinitialized)
    const storedId = localStorage.getItem(this.ACTIVE_CONVERSATION_ID_KEY);
    if (storedId) {
      this.currentConversationIdSubject.next(storedId);
      this.logger.info('ConversationManager', `Restored active conversation ID: ${storedId}`);
      return storedId;
    }

    // Generate new conversation ID
    const newId = this.generateConversationId();
    this.setActiveConversationId(newId); // Use setter to update both localStorage and BehaviorSubject
    this.logger.info('ConversationManager', `Created new active conversation ID: ${newId}`);
    return newId;
  }

  /**
   * Set the active conversation ID (used when restoring from backup)
   * 🔥 FIX (FIN-0065): This is now the ONLY way to change the active conversation
   * Updates both localStorage and the BehaviorSubject for reactive updates
   */
  setActiveConversationId(conversationId: string): void {
    localStorage.setItem(this.ACTIVE_CONVERSATION_ID_KEY, conversationId);
    this.currentConversationIdSubject.next(conversationId);
    this.logger.info('ConversationManager', `Set active conversation ID: ${conversationId}`);
  }

  /**
   * Get the current active conversation ID without creating a new one
   * 🔥 FIX (FIN-0065): Returns the in-memory value for instant access
   * This is synchronized with localStorage by setActiveConversationId()
   */
  getActiveConversationId(): string | null {
    return this.currentConversationIdSubject.getValue();
  }

  /**
   * Get the current active conversation ID synchronously (for compatibility)
   * Alias for getActiveConversationId()
   */
  getCurrentConversationId(): string | null {
    return this.currentConversationIdSubject.getValue();
  }

  /**
   * Clear all conversations (typically on logout or major state change)
   * 🔥 FIX (FIN-0065): Also clears the current conversation ID BehaviorSubject
   */
  clearAllConversations(): void {
    this.conversations.clear();
    this.conversationsSubject.next(new Map());
    this.currentConversationIdSubject.next(null);

    // Also clear stored conversation ID and backup
    localStorage.removeItem(this.ACTIVE_CONVERSATION_ID_KEY);
    localStorage.removeItem(this.CONVERSATION_BACKUP_KEY);
    localStorage.removeItem('connection-recovery-needed');

    this.logger.info('ConversationManager', 'Cleared all conversations and stored state');
  }

  /**
   * Get conversation statistics for debugging
   */
  getConversationStats(): { total: number; active: number; forClient: (userEmail: string) => number } {
    const total = this.conversations.size;
    const active = Array.from(this.conversations.values())
      .filter(context => context.isActive).length;

    return {
      total,
      active,
      forClient: (userEmail: string) => this.getConversationsForClient(userEmail).length
    };
  }
}